import { useLayoutEffect, useState } from 'react';
import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  TalentRediscoveryFilters,
  SearchFormData,
} from 'talent_search/shared/types';
import queryParams from 'talent_search/lib/query_params';

export type RediscoveryApplication = {
  id: number;
  fullName: string;
  appliedOn: string;
  hiringPlanId: number;
  hiringPlanName: string;
  currentStage: { id: number; name: string } | null;
  rejectedOn: string | null;
  rejectionReason: string | null;
  linkedinAddress: string | null;
  githubAddress: string | null;
  websiteAddress: string | null;
  referral: boolean;
  internal: boolean;
  activeOnAnotherJob: boolean;
  potentialDuplicate: boolean;
  tags: Array<{ label: string; value: number }>;
  candidateProfilePath: string;
};

export type RediscoveryRow = {
  id: number;
};

const useQueryParams = () => {
  const { control } = useFormContext<TalentRediscoveryFilters>();
  const formValues = useWatch({ control }) as TalentRediscoveryFilters;

  const transformedParams = queryParams(
    formValues as unknown as SearchFormData
  );

  return {
    ...transformedParams,
    page: formValues.page,
    page_size: formValues.perPage,
  };
};

export const useRediscoveryApplications = (
  path: string
): UseQueryResult<{
  rows: RediscoveryRow[];
  totalRows: number;
}> & {
  queryKey: string[];
} => {
  const queryClient = useQueryClient();
  const params = useQueryParams();

  const queryString = JSON.stringify(params);
  const currentQueryKey = ['rediscovery-applications', queryString];

  useLayoutEffect(() => {
    // Invalidate cache when filters change
    queryClient.invalidateQueries({
      queryKey: ['rediscovery-applications'],
      refetchType: 'none',
    });
  }, [queryString, queryClient]);

  // Keep totalRows from resetting to 0 during page loads
  const [totalRows, setTotalRows] = useState(0);

  const queryResult = useQuery({
    queryKey: currentQueryKey,
    queryFn: async () => {
      const { data } = await standardRequest.post(path, params);

      setTotalRows(data.total);

      // Populate individual application caches
      const rows: RediscoveryRow[] = data.applications.map(
        (application: RediscoveryApplication) => {
          queryClient.setQueryData(
            ['rediscovery-application', application.id],
            application
          );
          return { id: application.id };
        }
      );

      return { rows, totalRows: data.total };
    },
    placeholderData: {
      rows: [],
      totalRows: params.page === 1 ? 0 : totalRows,
    },
    staleTime: 1000 * 60 * 10,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    ...queryResult,
    queryKey: currentQueryKey,
  };
};

// Individual application hook for accessing cached application data
export const useRediscoveryApplication = (
  applicationId: number
): { application: RediscoveryApplication } => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['rediscovery-application', applicationId],
    staleTime: Infinity,
    queryFn: () =>
      queryClient.getQueryData([
        'rediscovery-application',
        applicationId,
      ]) as RediscoveryApplication,
    retry: 1,
  });

  return {
    application: query.data as RediscoveryApplication,
  };
};
